import React from 'react';
import { Box, Typography, Paper, Stack, TextField, Button, Alert } from '@mui/material';
import { useMutation, gql } from '@apollo/client';
import { useForm, Controller } from 'react-hook-form';

const CHANGE_MY_PASSWORD = gql`
  mutation ChangeMyPassword($currentPassword: String!, $newPassword: String!) {
    changeMyPassword(currentPassword: $currentPassword, newPassword: $newPassword)
  }
`;

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const AccountTab: React.FC = () => {
  const [changePassword, { loading }] = useMutation(CHANGE_MY_PASSWORD);
  const [status, setStatus] = React.useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = React.useState('');

  const { control, handleSubmit, reset } = useForm<PasswordForm>({
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onSubmit = async (values: PasswordForm) => {
    setStatus('idle');
    if (values.newPassword !== values.confirmPassword) {
      setStatus('error');
      setErrorMessage('New passwords do not match');
      return;
    }
    if (values.newPassword.length < 6) {
      setStatus('error');
      setErrorMessage('New password must be at least 6 characters');
      return;
    }
    try {
      await changePassword({
        variables: {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        },
      });
      setStatus('success');
      reset();
    } catch (err: any) {
      setStatus('error');
      setErrorMessage(err.message || 'Failed to change password');
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
          Change Password
        </Typography>

        {status === 'success' && (
          <Alert severity="success" sx={{ mb: 2 }}>Password updated successfully.</Alert>
        )}
        {status === 'error' && (
          <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Controller
              name="currentPassword"
              control={control}
              render={({ field }) => (
                <TextField {...field} type="password" label="Current password" size="small" fullWidth />
              )}
            />
            <Controller
              name="newPassword"
              control={control}
              render={({ field }) => (
                <TextField {...field} type="password" label="New password" size="small" fullWidth />
              )}
            />
            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <TextField {...field} type="password" label="Confirm new password" size="small" fullWidth />
              )}
            />
            <Button type="submit" variant="contained" size="small" disabled={loading} sx={{ alignSelf: 'flex-start' }}>
              Update Password
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default AccountTab;
